// Responsável, status, etiquetas e notas internas — Fase 14.
//
// É o bloco que transforma a caixa de entrada em ferramenta de EQUIPE. Sem ele
// duas pessoas respondem o mesmo cliente sem saber uma da outra — que é o
// problema real de uma caixa compartilhada, e não a falta de recurso.
//
// As três primeiras ações passam por RPC porque deixam rastro no audit log
// (ver `apiOrganizacao.ts`). A nota não precisa: ela já É o registro.
//
// ⚠️ NOTA INTERNA NÃO VAI PARA O CLIENTE. O aviso fica na tela, junto do campo,
//    e não só na documentação: quem digita ali está a um clique do composer,
//    que manda de verdade. A cor e o texto existem para essa fração de segundo.

import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, Lock, Plus, StickyNote, Tag, Trash2, UserRound, X } from 'lucide-react';
import type { ConversaLista, ConversaStatus } from '../tipos';
import type { Etiqueta, Membro, Nota } from '../apiOrganizacao';
import {
  apagarNota, atribuirConversa, carregarNotas, criarNota, marcarEtiqueta, mudarStatusConversa,
} from '../apiOrganizacao';
import { desdeQuando } from '../formato';

interface Props {
  conversa: ConversaLista;
  orgId: string;
  meuId: string;
  membros: Membro[];
  etiquetas: Etiqueta[];
  /** Ids das etiquetas já aplicadas nesta conversa. */
  etiquetasDaConversa: string[];
  onMudou: () => void;
}

const STATUS: { valor: ConversaStatus; rotulo: string }[] = [
  { valor: 'open', rotulo: 'Aberta' },
  { valor: 'pending', rotulo: 'Aguardando' },
  { valor: 'resolved', rotulo: 'Resolvida' },
];

export default function PainelTrabalho({
  conversa, orgId, meuId, membros, etiquetas, etiquetasDaConversa, onMudou,
}: Props) {
  const [notas, setNotas] = useState<Nota[]>([]);
  const [novaNota, setNovaNota] = useState('');
  const [salvandoNota, setSalvandoNota] = useState(false);
  const [escolhendoTag, setEscolhendoTag] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const recarregarNotas = useCallback(async () => {
    try {
      setNotas(await carregarNotas(conversa.id));
    } catch {
      // Nota é acessório: falhar ao listar não pode derrubar o painel inteiro.
      setNotas([]);
    }
  }, [conversa.id]);

  useEffect(() => { void recarregarNotas(); }, [recarregarNotas]);

  const comAviso = useCallback(async (acao: () => Promise<void>) => {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
      onMudou();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  }, [onMudou]);

  const aplicadas = new Set(etiquetasDaConversa);

  return (
    <div className="border-b border-gray-200 px-4 py-3.5 dark:border-slate-700">
      {erro && (
        <p className="mb-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-300">
          {erro}
        </p>
      )}

      {/* ─── Responsável ───────────────────────────────────────────────── */}
      <h4 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        <UserRound size={11} />
        Responsável
      </h4>
      <select
        value={conversa.assignee_id ?? ''}
        disabled={ocupado}
        onChange={(e) => void comAviso(() => atribuirConversa(conversa.id, e.target.value || null))}
        className="w-full rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
      >
        <option value="">Sem responsável (fila de todos)</option>
        {membros.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}{m.id === meuId ? ' (eu)' : ''}
          </option>
        ))}
      </select>
      {conversa.assignee_id && conversa.assignee_id !== meuId && (
        <button
          disabled={ocupado}
          onClick={() => void comAviso(() => atribuirConversa(conversa.id, meuId))}
          className="mt-1 text-[11px] font-medium text-primary transition-opacity hover:opacity-70"
        >
          assumir esta conversa
        </button>
      )}

      {/* ─── Status ───────────────────────────────────────────────────── */}
      <h4 className="mb-1.5 mt-3 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        Situação
      </h4>
      <div className="flex gap-1">
        {STATUS.map((s) => (
          <button
            key={s.valor}
            disabled={ocupado}
            onClick={() => void comAviso(() => mudarStatusConversa(conversa.id, s.valor))}
            className={`flex-1 rounded-lg px-1 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
              conversa.status === s.valor
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-600'
            }`}
          >
            {s.rotulo}
          </button>
        ))}
      </div>

      {/* ─── Etiquetas ─────────────────────────────────────────────────── */}
      <h4 className="mb-1.5 mt-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        <Tag size={11} />
        Etiquetas
      </h4>
      <div className="flex flex-wrap gap-1">
        {etiquetas.filter((t) => aplicadas.has(t.id)).map((t) => (
          <button
            key={t.id}
            disabled={ocupado}
            title="Remover etiqueta"
            onClick={() => void comAviso(() => marcarEtiqueta(conversa.id, t.id, false))}
            className="group flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white transition-opacity hover:opacity-80"
            style={{ backgroundColor: t.cor }}
          >
            {t.nome}
            <X size={9} className="opacity-60 group-hover:opacity-100" />
          </button>
        ))}

        {escolhendoTag ? (
          <select
            autoFocus
            value=""
            disabled={ocupado}
            onBlur={() => setEscolhendoTag(false)}
            onChange={(e) => {
              const id = e.target.value;
              setEscolhendoTag(false);
              if (id) void comAviso(() => marcarEtiqueta(conversa.id, id, true));
            }}
            className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
          >
            <option value="">escolha…</option>
            {etiquetas.filter((t) => !aplicadas.has(t.id)).map((t) => (
              <option key={t.id} value={t.id}>{t.nome}</option>
            ))}
          </select>
        ) : (
          <button
            onClick={() => setEscolhendoTag(true)}
            className="flex items-center gap-0.5 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] text-gray-500 transition-colors hover:border-primary hover:text-primary dark:border-slate-600 dark:text-slate-400"
          >
            <Plus size={9} />
            etiqueta
          </button>
        )}
      </div>
      {etiquetas.length === 0 && (
        <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">
          Nenhuma etiqueta cadastrada ainda. Um administrador cria em Atendimento.
        </p>
      )}

      {/* ─── Notas ─────────────────────────────────────────────────────── */}
      <h4 className="mb-1.5 mt-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-slate-500">
        <StickyNote size={11} />
        Notas internas
      </h4>

      <div className="rounded-xl bg-secondary/10 p-2">
        <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold text-[#8A5D0F] dark:text-secondary">
          <Lock size={9} />
          Só a equipe vê. O cliente não recebe.
        </p>
        <textarea
          rows={2}
          value={novaNota}
          onChange={(e) => setNovaNota(e.target.value)}
          placeholder="Cliente pediu retorno depois das 18h…"
          className="w-full resize-y rounded-lg border border-secondary/40 bg-white px-2 py-1.5 text-xs text-gray-800 outline-none focus:border-secondary dark:bg-slate-700 dark:text-slate-100"
        />
        <button
          disabled={!novaNota.trim() || salvandoNota}
          onClick={async () => {
            setSalvandoNota(true);
            setErro(null);
            try {
              await criarNota(orgId, conversa.id, novaNota.trim(), meuId);
              setNovaNota('');
              await recarregarNotas();
            } catch (e) {
              setErro((e as Error).message);
            } finally {
              setSalvandoNota(false);
            }
          }}
          className="mt-1.5 flex w-full items-center justify-center gap-1 rounded-lg bg-[#8A5D0F] px-2 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {salvandoNota ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
          Guardar nota
        </button>
      </div>

      {notas.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {notas.map((n) => (
            <li key={n.id} className="group rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-slate-900/40">
              <p className="whitespace-pre-wrap text-xs leading-snug text-gray-700 dark:text-slate-200">
                {n.body}
              </p>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <p className="text-[10px] text-gray-400 dark:text-slate-500">
                  {n.autor_nome ?? 'alguém'} · {desdeQuando(n.created_at)}
                </p>
                {n.author_id === meuId && (
                  <button
                    onClick={async () => {
                      await apagarNota(n.id);
                      await recarregarNotas();
                    }}
                    className="text-gray-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
